import { AlertCircle, Info, RotateCcw, X } from 'lucide-react'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { getTranscript } from '@/client/sdk.gen'
import type { ArtifactResponse } from '@/client/types.gen'
import { ArtifactEditModal } from '@/components/project/ArtifactEditModal'
import { ArtifactPreviewSkeleton } from '@/components/project/ArtifactPreviewSkeleton'
import { ArtifactViewerBody } from '@/components/project/artifact-viewer/ArtifactViewerBody'
import { getFileIconForArtifact } from '@/components/project/artifact-viewer/fileIcons'
import { PreviewCardHeader } from '@/components/project/artifact-viewer/PreviewCardHeader'
import { buildPreviewHeaderState } from '@/components/project/artifact-viewer/previewHeaderState'
import {
	exportTranscriptToPdf,
	type TranscriptExportMetadata,
} from '@/components/project/artifact-viewer/transcript/export'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useArtifactPreview } from '@/hooks/artifact-viewer'
import { useTranslation } from '@/hooks/useTranslation'
import {
	getArtifactPreviewCategory,
	getOriginalFileDownloadKind,
	isTranscriptMediaCategory,
	type OriginalFileDownloadKind,
} from '@/types/artifacts'
import { extractErrorMessage, isErrorHandledByInterceptor } from '@/utils/error'

const LABEL_BANNER_STORAGE_KEY = 'artifact-label-banner-dismissed'

const TRANSCRIPT_DOWNLOAD_LABEL_KEYS: Record<OriginalFileDownloadKind, string> = {
	audio: 'pages.projectDetail.transcript.downloadOriginal.audio',
	video: 'pages.projectDetail.transcript.downloadOriginal.video',
	image: 'pages.projectDetail.transcript.downloadOriginal.image',
	text: 'pages.projectDetail.transcript.downloadOriginal.text',
	document: 'pages.projectDetail.transcript.downloadOriginal.document',
}

function isArtifactUnlabeled(artifact: {
	time_dimension?: string | null
	purpose?: string | null
}): boolean {
	return !artifact.time_dimension && !artifact.purpose
}

export interface ArtifactPreviewPanelProps {
	projectId?: string
	artifactId?: string | null
	selectedArtifactName?: string | null
	onArtifactUpdated?: () => void
	refreshTrigger?: number
	getExportMetadata?: () => Promise<TranscriptExportMetadata>
	onArtifactEditUnsavedChange?: (hasUnsaved: boolean) => void
}

export function ArtifactPreviewPanel({
	projectId,
	artifactId,
	selectedArtifactName,
	onArtifactUpdated,
	refreshTrigger,
	getExportMetadata,
	onArtifactEditUnsavedChange,
}: ArtifactPreviewPanelProps) {
	const { t } = useTranslation()
	const [editModalOpen, setEditModalOpen] = useState(false)
	const [transcriptActiveTab, setTranscriptActiveTab] = useState<'transcript' | 'metadata'>(
		'transcript'
	)
	const [pdfActiveTab, setPdfActiveTab] = useState<'preview' | 'metadata'>('preview')
	const [imageActiveTab, setImageActiveTab] = useState<'preview' | 'metadata'>('preview')
	const [textActiveTab, setTextActiveTab] = useState<'preview' | 'metadata'>('preview')
	const [labelBannerDismissed, setLabelBannerDismissed] = useState(() => {
		if (typeof window === 'undefined' || !artifactId) return false
		return localStorage.getItem(`${LABEL_BANNER_STORAGE_KEY}-${artifactId}`) === 'true'
	})

	React.useEffect(() => {
		if (!artifactId) return
		setLabelBannerDismissed(
			localStorage.getItem(`${LABEL_BANNER_STORAGE_KEY}-${artifactId}`) === 'true'
		)
	}, [artifactId])

	const { artifact, loading, error, retry, handleDownload, downloading } = useArtifactPreview(
		projectId,
		artifactId,
		refreshTrigger
	)

	const [exportingTranscriptPdf, setExportingTranscriptPdf] = useState(false)

	const handleExportTranscriptPdf = useCallback(async () => {
		if (!projectId || !artifactId || !artifact) return

		setExportingTranscriptPdf(true)
		try {
			const response = await getTranscript({
				path: {
					project_id: projectId,
					artifact_id: artifactId,
				},
			})
			const transcript = response.data
			if (!transcript) {
				throw new Error(t('pages.projectDetail.transcript.exportError'))
			}
			if (transcript.status !== 'ready') {
				throw new Error(t('pages.projectDetail.transcript.exportNotReady'))
			}
			const hasContent =
				Boolean(transcript.content?.trim()) || (transcript.utterances?.length ?? 0) > 0
			if (!hasContent) {
				throw new Error(t('pages.projectDetail.transcript.noTranscript'))
			}

			const baseMeta = await getExportMetadata?.()
			await exportTranscriptToPdf(transcript, {
				...baseMeta,
				artifactName: baseMeta?.artifactName ?? artifact.name ?? undefined,
			})
		} catch (err: unknown) {
			if (!isErrorHandledByInterceptor(err)) {
				toast.error(extractErrorMessage(err, t('pages.projectDetail.transcript.exportError')))
			}
		} finally {
			setExportingTranscriptPdf(false)
		}
	}, [projectId, artifactId, artifact, getExportMetadata, t])

	const canExportTranscriptPdf = Boolean(
		artifact &&
			isTranscriptMediaCategory(getArtifactPreviewCategory(artifact)) &&
			artifact.status === 'completed'
	)

	const category = artifact ? getArtifactPreviewCategory(artifact) : null

	const headerState = useMemo(() => {
		if (!artifact || category === null) return null
		return buildPreviewHeaderState(
			artifact,
			category,
			{
				transcriptActiveTab,
				onTranscriptTabChange: setTranscriptActiveTab,
				pdfActiveTab,
				onPdfTabChange: setPdfActiveTab,
				imageActiveTab,
				onImageTabChange: setImageActiveTab,
				textActiveTab,
				onTextTabChange: setTextActiveTab,
			},
			{
				canExportPdf: canExportTranscriptPdf,
				onExportPdf: canExportTranscriptPdf ? handleExportTranscriptPdf : undefined,
				exportingPdf: exportingTranscriptPdf,
			}
		)
	}, [
		artifact,
		category,
		transcriptActiveTab,
		pdfActiveTab,
		imageActiveTab,
		textActiveTab,
		canExportTranscriptPdf,
		handleExportTranscriptPdf,
		exportingTranscriptPdf,
	])

	const previewArtifactDownload = useMemo(() => {
		if (!artifact) return null
		const kind = getOriginalFileDownloadKind(getArtifactPreviewCategory(artifact))
		return {
			labelKey: TRANSCRIPT_DOWNLOAD_LABEL_KEYS[kind],
			Icon: getFileIconForArtifact(artifact),
		}
	}, [artifact])

	const noopEdit = useCallback(() => {}, [])
	const noopDownload = useCallback(async () => {}, [])

	const handleDismissLabelBanner = () => {
		if (artifactId) {
			localStorage.setItem(`${LABEL_BANNER_STORAGE_KEY}-${artifactId}`, 'true')
			setLabelBannerDismissed(true)
		}
	}

	const panelCardClass = 'w-full md:w-2/3 flex flex-col min-h-0 gap-2 py-0 md:min-w-0'

	if (!projectId || !artifactId) {
		return (
			<Card className={panelCardClass}>
				<PreviewCardHeader
					selectedArtifactName={selectedArtifactName}
					artifact={null}
					headerState={null}
					previewArtifactDownload={null}
					onEdit={noopEdit}
					onDownload={noopDownload}
					downloading={false}
				/>
				<CardContent className="min-h-0 overflow-y-auto flex-1">
					<div className="p-4 bg-gray-50 rounded-lg">
						<p className="text-sm text-gray-500">{t('pages.projectDetail.preview.noFileSelected')}</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	if (loading && !artifact) {
		return (
			<Card className={panelCardClass}>
				<PreviewCardHeader
					selectedArtifactName={selectedArtifactName}
					artifact={null}
					headerState={null}
					previewArtifactDownload={null}
					onEdit={noopEdit}
					onDownload={noopDownload}
					downloading={false}
				/>
				<CardContent className="min-h-0 overflow-y-auto flex-1">
					<ArtifactPreviewSkeleton />
				</CardContent>
			</Card>
		)
	}

	if (error && !loading) {
		return (
			<Card className={panelCardClass}>
				<PreviewCardHeader
					selectedArtifactName={selectedArtifactName}
					artifact={null}
					headerState={null}
					previewArtifactDownload={null}
					onEdit={noopEdit}
					onDownload={noopDownload}
					downloading={false}
				/>
				<CardContent className="min-h-0 overflow-y-auto flex-1">
					<div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4" role="alert">
						<div className="flex items-start gap-3">
							<AlertCircle className="size-5 shrink-0 text-destructive mt-0.5" aria-hidden />
							<div className="flex-1 min-w-0">
								<h3 className="font-semibold text-destructive mb-1">
									{t('pages.projectDetail.artifactPreview.errorLoading')}
								</h3>
								<p className="text-sm text-red-700 dark:text-red-300 mb-4">{error}</p>
								<Button
									variant="outline"
									size="sm"
									onClick={retry}
									className="border-destructive/50 text-destructive hover:bg-destructive/10"
								>
									<RotateCcw className="size-3.5 mr-1.5" aria-hidden />
									{t('pages.projectDetail.artifactPreview.retry')}
								</Button>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		)
	}

	if (!artifact) {
		return (
			<Card className={panelCardClass}>
				<PreviewCardHeader
					selectedArtifactName={selectedArtifactName}
					artifact={null}
					headerState={null}
					previewArtifactDownload={null}
					onEdit={noopEdit}
					onDownload={noopDownload}
					downloading={false}
				/>
				<CardContent className="min-h-0 overflow-y-auto flex-1">
					<div className="p-4 bg-gray-50 rounded-lg">
						<p className="text-sm text-gray-500">{t('pages.projectDetail.preview.noFileSelected')}</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	const resolvedArtifact: ArtifactResponse = artifact
	const resolvedCategory = getArtifactPreviewCategory(resolvedArtifact)

	const showLabelBanner =
		resolvedArtifact.status === 'completed' &&
		isArtifactUnlabeled(resolvedArtifact) &&
		!labelBannerDismissed

	return (
		<Card className={panelCardClass}>
			<PreviewCardHeader
				selectedArtifactName={selectedArtifactName}
				artifact={resolvedArtifact}
				headerState={headerState}
				previewArtifactDownload={previewArtifactDownload}
				onEdit={() => setEditModalOpen(true)}
				onDownload={handleDownload}
				downloading={downloading}
			/>
			<CardContent className="min-h-0 overflow-y-auto flex-1">
				<div className="flex h-full min-h-0 flex-col">
					{showLabelBanner && (
						<div
							className="mb-4 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4"
							role="status"
						>
							<Info className="size-5 shrink-0 text-primary mt-0.5" aria-hidden />
							<div className="flex-1 min-w-0">
								<p className="text-sm text-foreground">
									{t('pages.projectDetail.artifactPreview.labelBannerMessage')}
								</p>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => setEditModalOpen(true)}
									className="mt-3"
								>
									{t('pages.projectDetail.artifactPreview.editMetadata')}
								</Button>
							</div>
							<button
								type="button"
								onClick={handleDismissLabelBanner}
								className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
								aria-label={t('common.close')}
							>
								<X className="size-4" />
							</button>
						</div>
					)}
					<ArtifactViewerBody
						projectId={projectId}
						artifactId={artifactId}
						artifact={resolvedArtifact}
						category={resolvedCategory}
						transcriptActiveTab={transcriptActiveTab}
						pdfActiveTab={pdfActiveTab}
						imageActiveTab={imageActiveTab}
						textActiveTab={textActiveTab}
						onDownload={handleDownload}
						downloading={downloading}
						onArtifactEditUnsavedChange={onArtifactEditUnsavedChange}
					/>
				</div>
				<ArtifactEditModal
					isOpen={editModalOpen}
					onClose={() => setEditModalOpen(false)}
					artifact={resolvedArtifact}
					projectId={projectId}
					onSuccess={() => {
						retry()
						onArtifactUpdated?.()
					}}
				/>
			</CardContent>
		</Card>
	)
}
